<template>
  <div class="candidate-card card" @click="$emit('click')">
    <div class="candidate-card-image no-image">
      <img
        :src="displayImageUrl"
        :alt="candidate.CANDIDATE_NAME"
        loading="lazy"
        @error="handleImageError"
      />
      <div class="candidate-status-badge" :class="statusClass">
        {{ candidate.STATUS }}
      </div>
    </div>
    <div class="candidate-card-body">
      <h4 class="candidate-name">{{ candidate.CANDIDATE_NAME }}</h4>
      <div class="external-link-row">
        <a
          v-for="link in externalLinks"
          :key="link.key"
          class="external-search-link"
          :class="`external-search-link--${link.key}`"
          :href="link.url"
          :aria-label="link.label"
          :title="link.label"
          target="_blank"
          rel="noopener noreferrer"
          @click.stop
        >
          <IconSvg :name="link.icon" custom-size="24px" decorative />
        </a>
      </div>
      <div class="candidate-specs">
        <span>予算: {{ formatCurrency(candidate.ESTIMATED_BUDGET) }}</span>
        <span>日数: {{ candidate.ESTIMATED_DAYS ? candidate.ESTIMATED_DAYS + '日' : '-' }}</span>
      </div>

      <div class="candidate-ratings">
        <div class="rating-summary">
          <span class="rating-label">平均</span>
          <span class="rating-val">{{ scoreLabel }}</span>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import IconSvg from "@/components/IconSvg.vue";
import { candidateExternalLinks, placeholderImage, resolveCandidateImage } from "@/utils/candidate-presentation.js";

export default {
  name: "CandidateCard",
  components: { IconSvg },
  props: {
    candidate: { type: Object, required: true },
    myScore: { type: Number, required: true },
    averageScore: { type: Number, required: true },
    candidateImageUrl: { type: String, default: "" },
  },
  emits: ["click"],
  setup(props) {
    const displayImageUrl = computed(() => resolveCandidateImage(props.candidate, props.candidateImageUrl));
    const externalLinks = computed(() => candidateExternalLinks(props.candidate?.CANDIDATE_NAME));


    const scoreCategories = ["sightseeing", "activity", "food", "cost"];

    const normalizeScoreDetails = (value) => {
      if (!value || typeof value !== "object") return null;
      const source = value.scores || value.scoreDetails || value.score_details || value;
      const details = {
        sightseeing: Number(source.sightseeingScore ?? source.sightseeing ?? source.tourism ?? 0) || 0,
        activity: Number(source.activityScore ?? source.activity ?? 0) || 0,
        food: Number(source.foodScore ?? source.food ?? source.gourmet ?? 0) || 0,
        cost: Number(source.costScore ?? source.cost ?? 0) || 0,
      };
      return scoreCategories.some((key) => details[key] > 0) ? details : null;
    };

    const scoreInfo = (value) => {
      const details = normalizeScoreDetails(value);
      if (details) {
        return { value: scoreCategories.reduce((sum, key) => sum + Number(details[key] || 0), 0), legacy: false };
      }
      const raw = value && typeof value === "object"
        ? Number(value.SCORE_VAL ?? value.scoreVal ?? value.score ?? 0) || 0
        : Number(value || 0) || 0;
      return raw > 0 ? { value: raw, legacy: true } : { value: 0, legacy: false };
    };

    const scoreLabel = computed(() => {
      const scores = props.candidate?.scores || [];
      const infos = scores.map(scoreInfo).filter((info) => info.value > 0);
      if (infos.length) {
        const avg = infos.reduce((sum, info) => sum + info.value, 0) / infos.length;
        return infos.some((info) => !info.legacy) ? `${avg.toFixed(1)}/20` : `旧評価 ${avg.toFixed(1)}/5`;
      }
      const fallback = Number(props.averageScore || 0);
      if (!fallback) return "-";
      return fallback <= 5 ? `旧評価 ${fallback.toFixed(1)}/5` : `${fallback.toFixed(1)}/20`;
    });
    const statusClass = computed(() => {
      const status = props.candidate?.STATUS;
      if (status === "決定") return "is-finalized";
      if (status === "却下") return "is-rejected";
      return "is-considering";
    });

    const formatCurrency = (val) => {
      if (val === null || val === undefined) return "-";
      return new Intl.NumberFormat("ja-JP", {
        style: "currency",
        currency: "JPY",
        maximumFractionDigits: 0,
      }).format(val);
    };

    const handleImageError = (event) => {
      event.target.src = placeholderImage;
    };


    return {
      displayImageUrl,
      externalLinks,
      scoreLabel,
      statusClass,
      formatCurrency,
      handleImageError,
    };
  },
};
</script>

<style scoped>
.candidate-card {
  overflow: hidden;
  cursor: pointer;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid var(--color-border);
  box-shadow: var(--card-shadow);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.25s ease;
}

.candidate-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.08);
  border-color: rgba(180, 207, 158, 0.35);
}

.candidate-card-image {
  height: 180px;
  position: relative;
  overflow: hidden;
}

.candidate-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.candidate-card:hover .candidate-card-image img { transform: scale(1.03); }

.candidate-status-badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  padding: 0.35rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 800;
  color: white;
  text-transform: uppercase;
  background-color: var(--primary-color);
}

.candidate-status-badge.is-considering {
  background-color: var(--primary-color);
}

.candidate-status-badge.is-finalized {
  background-color: var(--primary-color);
}

.candidate-status-badge.is-rejected {
  background-color: #ef4444;
}

.candidate-card-body { padding: 1.25rem; }

.candidate-name {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--text-main);
  margin: 0 0 0.5rem 0;
  font-family: 'Space Grotesk', sans-serif;
}

.external-link-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 0.75rem;
}

.external-search-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(30, 58, 95, 0.18);
  border-radius: 12px;
  background: #ffffff;
  text-decoration: none;
  transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
}

.external-search-link:hover {
  border-color: rgba(30, 58, 95, 0.48);
  box-shadow: 0 5px 12px rgba(15, 23, 42, 0.12);
  transform: translateY(-2px);
}

.external-search-link:active { transform: translateY(0) scale(0.96); }

.external-search-link:focus-visible {
  outline: 3px solid #1e3a5f;
  outline-offset: 3px;
}

.candidate-specs {
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-muted);
  margin-bottom: 1rem;
}

.candidate-ratings {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border-top: 1px solid rgba(226, 232, 240, 0.5);
  padding-top: 0.75rem;
  padding-inline: clamp(0.25rem, 2vw, 0.75rem);
}


.rating-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
}

.stars-display {
  display: flex;
  gap: 2px;
}

.star-icon {
  width: 14px;
  height: 14px;
  fill: #e2e8f0;
}

.star-icon.filled { fill: var(--accent-gold); }

.rating-val {
  color: var(--text-main);
  font-weight: 700;
}

.rating-val.unrated {
  color: var(--text-muted);
  font-weight: 500;
}
</style>

